import { Link } from "react-router-dom";
import {
  ArrowRight,
  Search,
  Pencil,
  Code2,
  Rocket,
  LifeBuoy,
  Check,
  Globe,
  ShoppingBag,
  LayoutTemplate,
  RefreshCw,
  Wrench,
  Clock,
} from "lucide-react";
import AuraGlow from "@/components/AuraGlow";
import FadeIn from "@/components/FadeIn";

const process = [
  {
    icon: Search,
    title: "Discovery",
    desc: "We learn about your business, audience and goals — and map out a plan that fits your timeline and budget.",
  },
  {
    icon: Pencil,
    title: "Design",
    desc: "Modern, on-brand design concepts — focused on clarity, trust and conversion from the very first scroll.",
  },
  {
    icon: Code2,
    title: "Development",
    desc: "Hand-built, fast-loading code with mobile-first responsiveness and a search-friendly foundation.",
  },
  {
    icon: Rocket,
    title: "Launch",
    desc: "We deploy, configure your domain, set up analytics and run the final QA pass — your site goes live.",
  },
  {
    icon: LifeBuoy,
    title: "Support",
    desc: "Optional ongoing care — content updates, performance tuning and small improvements every month.",
  },
];

const services = [
  {
    icon: Globe,
    title: "Business Website Design",
    desc: "Multi-page websites built around your brand and your customers.",
    bullets: ["Bespoke design", "Up to 6 core pages", "Lead capture", "On-page SEO"],
    timeline: "3–6 days",
  },
  {
    icon: ShoppingBag,
    title: "E-commerce Websites",
    desc: "Modern stores designed to sell — clean checkouts and beautiful product pages.",
    bullets: ["Product catalog", "Stripe checkout-ready", "Mobile-first UX", "Order management"],
    timeline: "7–10 days",
  },
  {
    icon: LayoutTemplate,
    title: "Landing Pages",
    desc: "Single-page conversion machines for campaigns, launches and lead generation.",
    bullets: ["Sharp copy structure", "A/B-ready blocks", "CRM-ready forms", "Tracking setup"],
    timeline: "2-5 days",
  },
  {
    icon: RefreshCw,
    title: "Website Redesigns",
    desc: "Reframe an outdated site into a modern, high-performing brand experience.",
    bullets: ["Brand & content audit", "Fresh modern design", "Speed improvements", "SEO migration"],
    timeline: "3–6 days",
  },
  {
    icon: Wrench,
    title: "Maintenance & Support",
    desc: "Stay fast, secure and current — without lifting a finger.",
    bullets: ["Monthly updates", "Backups & monitoring", "Small content edits", "Performance reviews"],
    timeline: "Monthly retainer",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-32 md:pt-40 pb-20" data-testid="services-hero">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <AuraGlow className="left-[15%] top-[40%]" size={520} blur={150} opacity={0.18} drift />
        <div className="container-7 relative">
          <FadeIn>
            <p className="overline">Services</p>
          </FadeIn>
          <FadeIn delay={120}>
            <h1 className="mt-4 max-w-4xl text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.05]">
              Everything you need to <span className="gradient-text">launch and grow</span> online.
            </h1>
          </FadeIn>
          <FadeIn delay={220}>
            <p className="mt-6 max-w-2xl text-lg text-[#94A3B8] leading-relaxed">
              From a single high-converting landing page to a full bespoke business site,
              we handle the design, build, launch and ongoing care — all under one roof.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* SERVICES DETAIL */}
      <section className="py-20 md:py-28" data-testid="services-detail">
        <div className="container-7 grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((s, i) => (
            <FadeIn key={s.title} delay={i * 80}>
              <div
                className="surface-card hover-card p-8 h-full"
                data-testid={`service-detail-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#1A2235] border border-[#1E293B] text-[#4DA3FF]">
                    <s.icon size={22} strokeWidth={1.6} />
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#94A3B8] border border-[#1E293B] px-3 py-1 rounded-full">
                    <Clock size={12} className="text-[#4DA3FF]" />
                    {s.timeline}
                  </span>
                </div>
                <h3 className="mt-5 text-xl font-medium">{s.title}</h3>
                <p className="mt-2 text-sm text-[#94A3B8]">{s.desc}</p>
                <ul className="mt-5 grid grid-cols-2 gap-2 text-sm text-[#94A3B8]">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Check size={14} className="text-[#4DA3FF] mt-1 shrink-0" strokeWidth={2} />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 md:py-28 bg-[#0E1422] border-y border-[#1E293B] relative overflow-hidden" data-testid="services-process">
        <AuraGlow className="-right-40 top-10" size={520} blur={160} opacity={0.12} />
        <div className="container-7 relative">
          <div className="max-w-2xl">
            <p className="overline">Our process</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-medium">
              From first call to launch — in 5 simple steps.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {process.map((p, i) => (
              <FadeIn key={p.title} delay={i * 100}>
                <div className="surface-card p-6 h-full relative" data-testid={`process-step-${i}`}>
                  <span className="absolute -top-3 left-6 px-2 py-0.5 text-[10px] tracking-[0.2em] uppercase font-medium rounded-full bg-[#4DA3FF] text-[#0B0F1A]">
                    Step {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#4DA3FF]/10 text-[#4DA3FF] mt-2">
                    <p.icon size={18} strokeWidth={1.6} />
                  </div>
                  <h4 className="mt-4 font-medium">{p.title}</h4>
                  <p className="mt-2 text-sm text-[#94A3B8]">{p.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING SUMMARY */}
      <section className="py-20 md:py-28" data-testid="services-pricing">
        <div className="container-7">
          <div className="max-w-2xl">
            <p className="overline">Pricing details</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-medium">
              Honest pricing. No surprises.
            </h2>
            <p className="mt-4 text-[#94A3B8]">
              Every project is quoted up front after your free consultation. Below is a
              guideline for typical small-business work.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              ["Starter", "£299–£499", "One-page site, mobile-ready, basic SEO."],
              ["Business", "£599–£999", "Multi-page, contact forms, on-page SEO, modern design."],
              ["Premium", "£1000+", "Full custom build, advanced features, branding & priority support."],
            ].map(([name, price, desc], i) => (
              <FadeIn key={name} delay={i * 100}>
                <div
                  className="surface-card p-7 hover-card"
                  data-testid={`services-pricing-card-${name.toLowerCase()}`}
                >
                  <p className="overline">{name}</p>
                  <p className="mt-3 font-display text-3xl">{price}</p>
                  <p className="mt-3 text-sm text-[#94A3B8]">{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" data-testid="services-cta">
        <div className="container-7">
          <div className="relative overflow-hidden surface-card p-10 md:p-14">
            <AuraGlow className="right-[20%] top-1/2 -translate-y-1/2" size={500} blur={150} opacity={0.18} />
            <div className="relative max-w-2xl">
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium">
                Let's build something modern.
              </h2>
              <p className="mt-4 text-[#94A3B8]">
                Tell us about your business — we'll come back with a plan, a quote and a launch date.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary" data-testid="services-cta-start">
                  Start a project
                  <ArrowRight size={16} />
                </Link>
                <Link to="/portfolio" className="btn-secondary" data-testid="services-cta-portfolio">
                  See our work
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
